import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useArtieScore } from "@/hooks/use-artie-score";
import { RecommendationCard } from "@/components/score/RecommendationCard";
import { GoDeeperDialog } from "@/components/score/GoDeeperDialog";
import { CompleteRecommendationDialog } from "@/components/score/CompleteRecommendationDialog";
import { Recommendation } from "@/types/artie-score";
import { useState } from "react";
import {
  Loader2,
  Youtube,
  Users,
  Lightbulb,
  ChevronRight,
  ChevronLeft,
  Download,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";

export const ReachPage = () => {
    const { projectId } = useParams();
    const { scoreData, loading, refresh } = useArtieScore(projectId);
    const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
    const [goDeeperOpen, setGoDeeperOpen] = useState(false);
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

    const { data: project } = useQuery({
        queryKey: ["project", projectId],
        queryFn: () => apiClient.getProject(projectId!),
        enabled: !!projectId,
    });

    if (loading) return (
        <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
    );
    
    if (!scoreData) return <div>No data available</div>;

    const reachMetrics = scoreData.reach_analysis;
    const hasYouTubeData = reachMetrics.current_subscribers !== undefined;

    // Filter recommendations for Reach-related items
    const reachRecommendations = scoreData.recommendations.filter(r =>
        (r.related_metric && r.related_metric.toLowerCase().includes('reach')) ||
        r.what_to_improve.toLowerCase().includes('reach') ||
        r.what_to_improve.toLowerCase().includes('subscriber') ||
        r.what_to_improve.toLowerCase().includes('view') ||
        r.what_to_improve.toLowerCase().includes('audience')
    );

    const handleAccept = async (recId: string) => {
        try {
            await apiClient.updateRecommendation(projectId!, {
                recommendation_id: recId,
                status: 'accepted',
            });
            await refresh();
        } catch (error) {
            console.error('Failed to accept recommendation:', error);
        }
    };

    const handleDecline = async (recId: string) => {
        try {
            await apiClient.updateRecommendation(projectId!, {
                recommendation_id: recId,
                status: 'declined',
            });
            await refresh();
        } catch (error) {
            console.error('Failed to decline recommendation:', error);
        }
    };

    const handleGoDeeper = (recId: string) => {
        const rec = reachRecommendations.find(r =>
            (r.id || r.what_to_improve) === recId
        );
        if (rec) {
            setSelectedRecommendation(rec);
            setGoDeeperOpen(true);
        }
    };

    const handleComplete = (recId: string) => {
        const rec = reachRecommendations.find(r =>
            (r.id || r.what_to_improve) === recId
        );
        if (rec) {
            setSelectedRecommendation(rec);
            setCompleteDialogOpen(true);
        }
    };

    // Helper to determine traffic light state
    const getStatusColor = (status: string) => {
        if (!status) return 'red'; // Default to red if status is missing
        const s = status.toLowerCase();
        
        // Exact matches first for our defined TrafficLight enum
        if (s === 'green') return 'green';
        if (s === 'yellow') return 'yellow';
        if (s === 'red') return 'red';

        // Fallback checks
        if (s.includes('high') || s.includes('good') || s.includes('optimal')) return 'green';
        if (s.includes('med') || s.includes('avg') || s.includes('fair')) return 'yellow';
        
        // Default based on backend status
        return 'red';
    };

    const statusColor = getStatusColor(scoreData.reach_status);
    
    // Format large numbers
    const formatNumber = (num: number) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
        return num.toString();
    };

    return (
        <div className="flex-1 flex flex-col items-center w-full min-w-0 relative">
            

            {/* Top Navigation */}
            <nav className="w-full max-w-5xl px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between z-30 sticky top-16 md:top-0 bg-background/80 backdrop-blur-md md:backdrop-blur-none md:bg-transparent mt-16 md:mt-0">
                <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                    <Link to={`/projects/${projectId}`} className="md:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground shrink-0">
                        <ChevronLeft className="w-5 h-5" />
                    </Link>
                    <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-ibm text-muted-foreground flex items-center gap-1 whitespace-nowrap overflow-hidden">
                            <Link to="/dashboard" className="hidden sm:inline hover:text-foreground cursor-pointer transition-colors">Dashboard</Link>
                            <ChevronRight className="hidden sm:block w-3 h-3 opacity-50" />
                            <Link to={`/projects/${projectId}`} className="hover:text-foreground cursor-pointer transition-colors truncate max-w-[100px] sm:max-w-[200px]">
                                {project?.title || "The Last Barista"}
                            </Link>
                            <ChevronRight className="w-3 h-3 opacity-50 flex-shrink-0" />
                            <span className="text-foreground font-medium truncate">Reach Analysis</span>
                        </span>
                    </div>
                    <h2 className="font-cabinet font-bold text-foreground text-lg sm:text-xl tracking-tight truncate">
                        Reach Potential
                    </h2>
                    </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0">
                     <button className="px-3 py-1.5 bg-secondary text-foreground rounded-lg text-xs font-medium border border-border hover:bg-border transition-colors flex items-center gap-2">
                        <Download className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Export Report</span>
                    </button>
                </div>
            </nav>

            <main className="flex-1 z-10 flex flex-col w-full max-w-5xl px-4 sm:px-6 pb-20 items-center">
        
            {/* Top Status Card */}
            <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                
                {/* Traffic Light & Overall Status */}
                <div className="lg:col-span-2 p-6 bg-card border border-border rounded-2xl flex flex-col sm:flex-row gap-8 items-center sm:items-start animate-in slide-in-from-bottom-4 duration-500 relative overflow-hidden">
                    {/* Decorative background blob */}
                    <div className="absolute top-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl opacity-50 translate-x-1/3 -translate-y-1/3 -z-0 pointer-events-none"></div>

                    {/* Traffic Light Visual */}
                    <div className="flex flex-col gap-2 shrink-0 z-10">
                        <div className="w-16 p-2 bg-foreground rounded-full flex flex-col items-center gap-2 shadow-xl border border-border">
                            {/* Red (Off/On) */}
                            {statusColor === 'red' ? (
                                <div className="w-12 h-12 rounded-full bg-red-500 shadow-[0_0_20px_rgba(239,68,68,0.5)] border border-red-400/50 flex items-center justify-center animate-pulse">
                                    <Check className="text-white opacity-90 w-6 h-6" />
                                </div>
                            ) : (
                                <div className="w-12 h-12 rounded-full bg-red-500/20 border border-red-500/10"></div>
                            )}

                            {/* Yellow (Off/On) */}
                            {statusColor === 'yellow' ? (
                                <div className="w-12 h-12 rounded-full bg-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.5)] border border-yellow-400/50 flex items-center justify-center animate-pulse">
                                    <Check className="text-white opacity-90 w-6 h-6" />
                                </div>
                            ) : (
                                <div className="w-12 h-12 rounded-full bg-yellow-500/20 border border-yellow-500/10"></div>
                            )}

                            {/* Green (Off/On) */}
                            {statusColor === 'green' ? (
                                <div className="w-12 h-12 rounded-full bg-green-500 shadow-[0_0_20px_rgba(34,197,94,0.5)] border border-green-400/50 flex items-center justify-center animate-pulse">
                                    <Check className="text-white opacity-90 w-6 h-6" />
                                </div>
                            ) : (
                                <div className="w-12 h-12 rounded-full bg-green-500/20 border border-green-500/10"></div>
                            )}
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-center text-muted-foreground mt-1">Status</span>
                    </div>

                    {/* Text Content */}
                    <div className="flex flex-col h-full justify-center z-10 w-full">
                        <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wide ${
                                statusColor === 'green' ? 'bg-green-500/10 text-green-600 border-green-500/20' :
                                statusColor === 'yellow' ? 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' :
                                'bg-red-500/10 text-red-600 border-red-500/20'
                            }`}>
                                {scoreData.reach_status} Potential
                            </span>
                            <span className="text-xs text-muted-foreground font-ibm">Updated just now</span>
                        </div>
                        <h1 className="text-3xl font-cabinet font-semibold text-foreground mb-2 tracking-tight">
                            {reachMetrics.expected_reach_range 
                                ? `Reach Projection: ${formatNumber(reachMetrics.expected_reach_range.low)} - ${formatNumber(reachMetrics.expected_reach_range.high)}`
                                : "Reach Projection: Data Unavailable"}
                        </h1>
                        <p className="text-sm text-muted-foreground font-ibm leading-relaxed max-w-lg mb-6">
                            {reachMetrics.gap_analysis || "Analysis of audience size and engagement potential based on current channel metrics and script quality."}
                        </p>
                        
                        {/* Stats Grid Mini */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 w-full">
                            <div className="p-3 bg-background border border-border rounded-xl">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold mb-1">Baseline Reach</p>
                                <p className="text-lg font-cabinet font-semibold">
                                    {/* Calculate the effective baseline used (Projection / Modifier) to ensure math aligns visually */}
                                    {reachMetrics.expected_reach_range?.mid && reachMetrics.content_quality_modifier
                                        ? formatNumber(Math.round(reachMetrics.expected_reach_range.mid / reachMetrics.content_quality_modifier))
                                        : (reachMetrics.avg_views ? formatNumber(reachMetrics.avg_views) : "N/A")}
                                    <span className="text-xs font-normal text-muted-foreground ml-1">avg</span>
                                </p>
                            </div>
                            <div className="p-3 bg-background border border-border rounded-xl">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold mb-1">Quality Mod</p>
                                <p className={`text-lg font-cabinet font-semibold ${
                                    (reachMetrics.content_quality_modifier || 1) >= 1 ? "text-green-600" : "text-red-600"
                                }`}>
                                    {reachMetrics.content_quality_modifier
                                        ? `${(reachMetrics.content_quality_modifier >= 1 ? "+" : "")}${Math.round(((reachMetrics.content_quality_modifier || 1) - 1) * 100)}%`
                                        : "0%"}
                                </p>
                            </div>
                            <div className="p-3 bg-background border border-border rounded-xl">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold mb-1">Target</p>
                                <p className="text-lg font-cabinet font-semibold text-primary">
                                    {reachMetrics.sufficient_for_target ? "Met" : "Unmet"}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* YouTube Connection Nudge */}
                {!hasYouTubeData ? (
                    <div className="lg:col-span-1 flex flex-col h-full animate-in slide-in-from-bottom-5 duration-700 delay-100">
                        <div className="flex-1 p-6 bg-card border border-border rounded-2xl flex flex-col items-center justify-center text-center relative overflow-hidden group">
                             
                             <div className="relative z-10 w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                                <Youtube className="w-6 h-6 text-red-500" />
                             </div>
                             <h3 className="font-cabinet font-semibold text-lg relative z-10">Connect YouTube</h3>
                             <p className="text-xs text-muted-foreground mt-2 mb-4 max-w-[200px] leading-relaxed relative z-10">
                                 Estimates are 80% more accurate with channel history data.
                             </p>
                             <Button 
                                variant="default"
                                className="relative z-10 bg-foreground text-background hover:opacity-90 transition-opacity"
                             >
                                 Connect Account
                             </Button>
                        </div>
                    </div>
                ) : (
                    <div className="lg:col-span-1 flex flex-col h-full animate-in slide-in-from-bottom-5 duration-700 delay-100">
                         <div className="flex-1 p-6 bg-card border border-border rounded-2xl flex flex-col items-center justify-center text-center relative overflow-hidden group">
                             <div className="absolute top-0 right-0 w-32 h-32 bg-secondary rounded-full blur-2xl opacity-30 -translate-y-1/2 translate-x-1/2"></div>
                             
                             <div className="relative z-10 w-12 h-12 bg-secondary rounded-xl flex items-center justify-center mb-4">
                                <Users className="w-6 h-6 text-foreground" />
                             </div>
                             <h3 className="font-cabinet font-semibold text-lg relative z-10">Audience Data</h3>
                             <div className="mt-4 grid grid-cols-2 gap-4 w-full">
                                <div className="text-center">
                                    <p className="text-[10px] uppercase text-muted-foreground tracking-wide font-semibold">Subscribers</p>
                                    <p className="text-lg font-bold">{formatNumber(reachMetrics.current_subscribers || 0)}</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-[10px] uppercase text-muted-foreground tracking-wide font-semibold">Engagement</p>
                                    <p className="text-lg font-bold">{reachMetrics.engagement_rate ? `${(reachMetrics.engagement_rate * 100).toFixed(1)}%` : "N/A"}</p>
                                </div>
                             </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Breakdown & Analysis Grid */}
            <div className="w-full mb-8">
                
                {/* Cold Start Guidance */}
                <div className="p-6 bg-card border border-border rounded-2xl animate-in slide-in-from-bottom-6 duration-700 delay-200 flex flex-col">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                            <Lightbulb className="w-4 h-4" />
                        </div>
                        <div>
                            <h3 className="font-cabinet font-semibold text-lg leading-tight">Strategy Insight</h3>
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Recommendation</span>
                        </div>
                    </div>
                    
                    <div className="flex-1 bg-secondary/50 rounded-xl p-4 border border-border mb-4">
                        <p className="text-sm font-ibm text-foreground leading-relaxed">
                            <span className="font-semibold">Insight:</span> {
                                reachMetrics.content_quality_modifier && reachMetrics.content_quality_modifier > 1
                                ? `Your script's strong narrative elements are projected to boost your baseline reach by ${Math.round((reachMetrics.content_quality_modifier - 1) * 100)}%.`
                                : reachMetrics.content_quality_modifier && reachMetrics.content_quality_modifier < 1
                                ? "Niche themes may limit broader appeal. Consider broadening the hook to capture a wider audience."
                                : "Your projected reach aligns with your channel's historical performance. Focus on promotion to drive additional growth."
                            }
                        </p>
                    </div>

                    <div className="space-y-3">
                        {reachMetrics.detailed_metrics?.traffic_discovery?.traffic_sources?.[0] && (
                            <div className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    <span className="font-semibold text-foreground">Top Traffic Source:</span> {reachMetrics.detailed_metrics.traffic_discovery.traffic_sources[0]}
                                </p>
                            </div>
                        )}
                        
                        {reachMetrics.detailed_metrics?.engagement_retention?.avg_view_duration && (
                            <div className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    <span className="font-semibold text-foreground">Avg View Duration:</span> {reachMetrics.detailed_metrics.engagement_retention.avg_view_duration}
                                </p>
                            </div>
                        )}

                        {/* Fallback if no detailed metrics */}
                        {!reachMetrics.detailed_metrics?.traffic_discovery && (
                            <>
                                <div className="flex items-start gap-3">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        {hasYouTubeData
                                            ? "Connect detailed analytics to see Traffic Sources."
                                            : "Connect YouTube to unlock audience demographic data."}
                                    </p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        {hasYouTubeData
                                            ? "Focus on increasing Average View Duration to boost organic reach."
                                            : "Early traction will rely heavily on external promotion."}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Reach-Specific Recommendations */}
            {reachRecommendations.length > 0 && (
                <div className="w-full animate-in slide-in-from-bottom-8 duration-700 delay-300">
                    <h3 className="font-cabinet font-semibold text-lg mb-4 flex items-center gap-2">
                        Reach Recommendations
                        <span className="bg-secondary text-muted-foreground text-xs py-0.5 px-2 rounded-full border border-border">{reachRecommendations.length}</span>
                    </h3>

                    <div className="flex flex-col gap-3">
                        {reachRecommendations.map((rec, idx) => (
                            <RecommendationCard
                                key={idx}
                                recommendation={rec}
                                projectId={projectId!}
                                onAccept={handleAccept}
                                onDecline={handleDecline}
                                onGoDeeper={handleGoDeeper}
                                onComplete={handleComplete}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Dialogs */}
            {selectedRecommendation && (
                <>
                    <GoDeeperDialog
                        open={goDeeperOpen}
                        onOpenChange={setGoDeeperOpen}
                        recommendation={selectedRecommendation}
                        projectId={projectId!}
                        onUpdate={refresh}
                    />
                    <CompleteRecommendationDialog
                        open={completeDialogOpen}
                        onOpenChange={setCompleteDialogOpen}
                        recommendation={selectedRecommendation}
                        projectId={projectId!}
                        onComplete={() => {
                            refresh();
                            setCompleteDialogOpen(false);
                        }}
                    />
                </>
            )}

            </main>
        </div>
    );
};